import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import { useApiCall } from './apiClient/apiClient';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import PageNotFound from './components/PageNotFound/PageNotFound';
import { COCKPIT_V3 } from './config/constants';
import { rbacApi } from './pages/CreateOrg/TenantManagement/RbacSettings/api/rbacApi';
import {
  setRbacAccess,
  setRbacIsSuperAdmin,
  setRbacLoaded,
} from './redux/slices/rbacSlice';
import { routes } from './routes/routes';
import { checkUserAccess, parseLoggedInUserRbac } from './utils/rbacFlags';

const App = () => {
  // const navigate = useNavigate();

  const [tokens, setTokens] = useState({ accessToken: null, idToken: null });
  const storedIdToken = localStorage?.getItem('idToken');
  // const storedAccessToken = localStorage?.getItem('accessToken');
  const rawAdminDetails = localStorage?.getItem('adminDetails');
  const parsedAdminDetails = (() => {
    try {
      return rawAdminDetails ? JSON.parse(rawAdminDetails) : null;
    } catch (_) {
      return null;
    }
  })();
  const [isAdminLoaded, setIsAdminLoaded] = useState(
    Boolean(parsedAdminDetails?.isDataStored)
  );

  const isAuthenticated = useIsAuthenticated();

  const { instance, inProgress } = useMsal();
  const interactionStatus = inProgress;
  const dispatch = useDispatch();
  const rbacState = useSelector(state => state.rbac);

  const { callApi } = useApiCall();

  useEffect(() => {
    if (isAuthenticated && isAdminLoaded) {
      (async () => {
        try {
          const accessData = await rbacApi.getLoggedInUserAccess(callApi);
          const { isSuperAdmin, access } = parseLoggedInUserRbac(
            accessData,
            parsedAdminDetails?.data
          );
          dispatch(setRbacIsSuperAdmin(isSuperAdmin));
          dispatch(setRbacAccess(access));
        } catch (error) {
          console.log(error);
          dispatch(setRbacIsSuperAdmin(false));
          dispatch(setRbacAccess([]));
        } finally {
          // Mark loaded on success or failure so gated pages stop waiting
          // on an empty pre-fetch state (which denies all flags).
          dispatch(setRbacLoaded(true));
        }
      })();
    }
  }, [isAuthenticated, isAdminLoaded, callApi, dispatch]);

  const fetchAdminDetails = useCallback(
    async data => {
      try {
        const responseData = await callApi(
          'POST',
          `${COCKPIT_V3}/admin-details`,
          {
            id_token: data,
          }
        );
        localStorage.setItem(
          'adminDetails',
          JSON.stringify({
            data: responseData?.data,
            isDataStored: true,
          })
        );
      } catch (error) {
        // Swallow to avoid blocking routing; optional error UI could be added
      }
    },
    [callApi]
  );

  function getMsalInstance() {
    return window.__MSAL_INSTANCE__;
  }

  const getTokens = useCallback(async () => {
    const msalInstance = getMsalInstance();
    const accounts = await msalInstance.getAllAccounts();

    // if (!accounts?.length) {
    //   throw new Error("No accounts found, user needs to log in");
    // }

    const tokenRequest = {
      scopes: [
        'openid',
        'offline_access',
        process.env.REACT_APP_MSAL_CLIENT_ID || '',
      ],
      account: accounts?.[0],
    };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      if (response.accessToken?.length > 0) {
        localStorage.setItem('accessToken', response?.accessToken);
        setTokens(prev => ({ ...prev, accessToken: response?.accessToken }));
      }
      if (response.idToken?.length > 0) {
        setTokens(prev => ({ ...prev, idToken: response?.idToken }));
        localStorage.setItem('idToken', response?.idToken);
      }
      return { accessToken: response?.accessToken, idToken: response?.idToken };
    } catch (error) {
      await msalInstance.loginRedirect(); // or loginPopup
      throw new Error('Authentication required');
    }
  }, []);
  useEffect(() => {
    // const currentPath = window?.location?.pathname;

    if (!isAuthenticated && interactionStatus === 'none') {
      instance.loginRedirect();
    }
  }, [interactionStatus, isAuthenticated, instance]);
  useEffect(() => {
    if (isAuthenticated && interactionStatus === 'none') {
      getTokens();
    }
  }, [isAuthenticated, interactionStatus, getTokens]);
  useEffect(() => {
    const idTok = tokens?.idToken || storedIdToken;
    if (!isAdminLoaded && idTok) {
      (async () => {
        try {
          await fetchAdminDetails(idTok);
        } finally {
          setIsAdminLoaded(true);
        }
      })();
    }
  }, [tokens?.idToken, storedIdToken, isAdminLoaded, fetchAdminDetails]);
  //   useEffect(() => {
  //     if (isAuthenticated) {
  //       const storedRoute = localStorage.getItem('redirectRoute');
  //       if (!window?.location?.pathname.includes('/candidate/review-offer')) {
  //         // alert("token", isAuthenticated);
  //         // fetchData();
  //       }
  //       if (storedRoute && storedRoute !== window.location.pathname) {
  //         navigate(storedRoute);
  //         localStorage.removeItem('redirectRoute');
  //       }
  //     }
  //   }, [isAuthenticated]);

  if (inProgress !== 'none') {
    return (
      <div className='flex flex-col justify-center items-center h-screen w-full'>
        <div className='w-64'>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (isAuthenticated && isAdminLoaded) {
    return (
      <Layout>
        <ToastContainer
          position='top-right'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          theme='light'
        />

        <Routes>
          <Route path='/' element={<Navigate to='/dashboard' replace />} />
          {routes
            .filter(item => {
              if (item.requiredAccess) {
                return checkUserAccess(item.requiredAccess, rbacState);
              }
              if (item.superAdminOnly) {
                return rbacState.isLoaded && rbacState.isSuperAdmin;
              }
              return true;
            })
            .map(item => (
              <Route
                key={item?.key}
                path={item?.route}
                element={item?.component}
              />
            ))}

          <Route path='*' element={<PageNotFound />} />
        </Routes>
      </Layout>
    );
  }

  // Fallback while loading admin details or during login
  return (
    <div className='flex flex-col justify-center items-center h-screen w-full'>
      <div className='w-64'>
        <LoadingSpinner />
      </div>
    </div>
  );
};

export default App;
